import { DateTime } from "../imports";
import { maxDataPoints } from "../graphs/base_graphs";
import { apiURL, observatoryMetadata } from "../config";
import { parseRamotsweDatetime, getEarliestDate } from "../utils";
export let responseAPI = {};

/**
 * Gets the all sky image for the given observatory.
 * @param {string} observatoryName
 * @returns {Object}
 */
export async function getAllSkyImage(observatoryName) {
  const imageType = "allsky";
  const endpoint = "observatoryimage";

  return await getImage(endpoint, observatoryName, imageType);
}

/**
 * Gets Musca data from the Ramotswe API for the main page of the dashboard.
 * @param {string} observatoryName
 * @returns {Object}
 */

const addUnitString = (api, unitString) => {
  if (api == null) {
    return null;
  }
  return api + unitString;
};

export async function getMuscaData(observatoryName) {
  await getData("musca", "musca", observatoryName);
  const key = "musca" + observatoryName;
  const apiData = responseAPI.hasOwnProperty(key) ? responseAPI[key] : null;

  const domeConversion = (api) => {
    if (api < 0) {
      return "Closed";
    } else if (api === 0) {
      return "Part Open";
    } else if (0 <= api && api <= 3) {
      return "Open";
    } else if (api === 5) {
      return "Illegal State";
    }
    return null;
  };
  const doorConversion = (api) => {
    if (api == 0) {
      return "Open";
    }
    if (api == 1) {
      return "Closed";
    }
    return null;
  };

  return {
    exportDatetime: parseRamotsweDatetime(apiData?.datetime_utc),
    dome_position: domeConversion(apiData?.dome_position),
    door_position: doorConversion(apiData?.door_position),
    battery_voltage: addUnitString(apiData?.battery_voltage, " V"),
  };
}

/**
 * Gets Cumulus data from Ramotswe API for the main page of the dashboard.
 * @param {string} observatoryName
 * @returns {Object}
 */
export async function getCumulusData(observatoryName) {
  await getData("cumulus", "cumulus", observatoryName);
  const key = "cumulus" + observatoryName;
  const apiData = responseAPI.hasOwnProperty(key) ? responseAPI[key] : null;

  let current_wind = addUnitString(apiData?.latest_wind_speed, " km/h");
  if (current_wind !== null && apiData.latest_wind_speed > 0) {
    current_wind =
      current_wind +
      " at " +
      addUnitString(apiData?.current_wind_bearing, " \u00B0");
  }
  if (current_wind !== null) {
    current_wind = "Current: " + current_wind;
  }

  return {
    exportDatetime: parseRamotsweDatetime(apiData?.datetime_utc),
    temperature: addUnitString(apiData?.temperature, "\u00B0 C"),
    humidity: addUnitString(apiData?.humidity, "%"),
    wind_speed_moving_average: addUnitString(
      apiData?.wind_speed_moving_average,
      " km/h"
    ),
    rainfall_today: addUnitString(apiData?.rainfall_today, " mm"),
    pressure_sea_level: addUnitString(apiData?.pressure_sea_level, " hPa"),
    current_wind: current_wind,
  };
}

/**
 * Gets CloudWatcher data from the Ramotswe API for the main page of the dashboard.
 * @param {string} observatoryName
 * @returns {Object}
 */
export async function getCloudwatcherData(observatoryName) {
  await getData("cloudwatcher", "cloudwatcher", observatoryName);
  const key = "cloudwatcher" + observatoryName;
  const apiData = responseAPI.hasOwnProperty(key) ? responseAPI[key] : null;

  return {
    exportDatetime: parseRamotsweDatetime(apiData?.datetime_utc),
    clarity: apiData?.clarity_label,
    dryness: apiData?.dryness_label,
    darkness: apiData?.darkness_label,
  };
}

/**
 * Gets the battery state from the Ramotswe API for the main page of the dashboard.
 * @param {string} observatoryName
 * @returns {Object}
 */
export async function getBatteryState(observatoryName) {
  await getData(
    "batteryState",
    "solar/battery_charging_state",
    observatoryName
  );
  const key = "batteryState" + observatoryName;
  const apiData = responseAPI.hasOwnProperty(key) ? responseAPI[key] : null;

  return {
    battery_state: apiData?.battery_state,
    exportDatetime: parseRamotsweDatetime(apiData?.datetime_utc),
  };
}

/**
 * Gets the battery state of charge from the Ramotswe API for the main page of the dashboard.
 * @param {string} observatoryName
 * @returns {Object}
 */
export async function getBatteryCharge(observatoryName) {
  await getData("batteryCharge", "solar/battery_soc_instant", observatoryName);
  const responseKey = "batteryCharge" + observatoryName;
  const apiData =
    responseAPI.hasOwnProperty(responseKey) && responseAPI[responseKey] !== null
      ? responseAPI[responseKey]
      : null;

  // return null if the request gave no data (no batteries)
  if (apiData === null) {
    return null;
  }

  const batteryDataArray = [];
  const exportDatetimeArray = [];
  Object.entries(responseAPI[responseKey]).forEach((entry) => {
    const [key, value] = entry;
    if (key.includes("battery_state_of_charge")) {
      batteryDataArray.push(value);
    } else if (key.includes("datetime_utc")) {
      exportDatetimeArray.push(parseRamotsweDatetime(value));
    }
  });

  // return null if we found no data
  if (batteryDataArray.length == 0) {
    return { battery_state_of_charge: null, exportDatetime: null };
  }

  return {
    battery_state_of_charge: batteryDataArray.join("%, ") + "%", // convert to string
    exportDatetime: getEarliestDate(exportDatetimeArray).toString(), // return the oldest datetime
  };
}

/**
 * Fetch function for the moon/sun being above the horizon
 * @param {string} observatoryName
 * @returns {Object}
 */
export async function getSunMoonVisible(observatoryName) {
  await getData(
    "sunMoonHorizon",
    "astronomical/ephemeris_target_visible",
    observatoryName
  );
  const key = "sunMoonHorizon" + observatoryName;
  const apiData = responseAPI.hasOwnProperty(key) ? responseAPI[key] : null;

  return {
    sun_visibility: apiData?.sun,
    moon_visibility: apiData?.moon,
  };
}

export async function getSunMoonAltitudes(observatoryName) {
  const now = DateTime.now().setZone("UTC");
  const start_time = now.minus({ minutes: 20 });

  const data = await getHistoryData(
    observatoryName,
    "telemetrytimeframedata",
    start_time,
    now,
    {
      model: "Ephemeris",
      column_names: ["target_alt"],
      filter_column_names: "target_name",
      filter_values: "sun",
    }
  );

  const moonData = await getHistoryData(
    observatoryName,
    "telemetrytimeframedata",
    start_time,
    now,
    {
      model: "Ephemeris",
      column_names: ["target_alt"],
      filter_column_names: "target_name",
      filter_values: "moon",
    }
  );

  return {
    sun: addUnitString(Math.round(data[0].target_alt * 10) / 10, "\u00B0"),
    moon: addUnitString(Math.round(moonData[0].target_alt * 10) / 10, "\u00B0"),
  };
}

/**
 * Query Ramotswe for sunrise/sunset times for a given UTC day
 * @param {string} observatoryName
 * @param {string} ISODate
 * @returns {Object}
 */
async function getSunTimingsByDay(observatoryName, ISODate) {
  await getData(
    "sunTimings" + ISODate,
    "astronomical/sunrise_sunset",
    observatoryName,
    makeURLSearchParams({
      timestamp: ISODate,
    })
  );
  const key = "sunTimings" + ISODate + observatoryName;
  return responseAPI.hasOwnProperty(key) ? responseAPI[key] : {};
}

/**
 * Get the next sunrise/set event time and what it will be
 * @param {string} observatoryName
 * @returns {Object}
 */
export async function getSunTimings(observatoryName) {
  const now = DateTime.now().setZone("UTC");
  const result = {
    next_sunrise: null,
    next_sunset: null,
    time_until: null,
    next_event: null,
  };

  // get sun timings for the next few days
  const dateTimes = [now, now.plus({ days: 1 })]; //, now.plus({ days: 2 })];
  const allTimings = [];
  for (let dt of dateTimes) {
    // fetch the timings
    const dtArray = await getSunTimingsByDay(observatoryName, dt);

    // store those timings
    if (dtArray["sunrises"] !== null) {
      for (let dt of dtArray["sunrises"]) {
        allTimings.push({
          type: "sunrise",
          time: DateTime.fromISO(dt, { zone: "UTC" }),
        });
      }
    }
    if (dtArray["sunsets"] !== null) {
      for (let dt of dtArray["sunsets"]) {
        allTimings.push({
          type: "sunset",
          time: DateTime.fromISO(dt, { zone: "UTC" }),
        });
      }
    }
  }

  // keep only events that have yet to pass
  const upcomingEvents = allTimings.filter((e) => e.time - now > 0);

  if (upcomingEvents.length === 0) {
    return result;
  }

  // get the next event to come (of any type), and save it's data
  upcomingEvents.sort((a, b) => a.time - b.time);
  const nextEvent = upcomingEvents[0];
  result.next_event = nextEvent.type;
  result.time_until = nextEvent.time.diff(now).toFormat("h:mm");

  // save the next sunrise and sunset specifically
  const nextSunriseDT = upcomingEvents.filter((e) => e.type === "sunrise")[0]
    .time;
  const nextSunsetDT = upcomingEvents.filter((e) => e.type === "sunset")[0]
    .time;

  result.next_sunrise = nextSunriseDT;
  result.next_sunset = nextSunsetDT;

  return result;
}

/**
 * Get information about the current moon phase
 * @param {string} observatoryName
 * @returns {Object}
 */
export async function getMoonPhase(observatoryName) {
  const endDateTime = DateTime.now().setZone("UTC");
  const startDateTime = endDateTime.minus({ minutes: 15 });

  const data = await getHistoryData(
    observatoryName,
    "telemetrytimeframedata",
    startDateTime,
    endDateTime,
    {
      model: "Ephemeris",
      column_names: ["moon_phase", "moon_phase_angle", "moon_illumination"],
      filter_column_names: "target_name",
      filter_values: "moon",
    }
  );

  return data.at(-1);
}

/**
 * Get the wind rose data for an observatory over a given number of weeks (length).
 * @param {string} observatoryName
 * @param {Object} timeRange
 * @returns {Object[]}
 */
export async function getWindRoseData(observatoryName, timeRange) {
  const responseAPIKey = "WindRoseData";

  const formatDateTime = (dt) => dt.toISO().slice(0, -5);
  const endDateTime = DateTime.now().setZone("UTC");
  const startDateTime = endDateTime.minus(timeRange);
  const searchParams = {
    start_time: formatDateTime(startDateTime),
    end_time: formatDateTime(endDateTime),
  };

  await getData(
    responseAPIKey,
    "windrose",
    observatoryName,
    makeURLSearchParams(searchParams)
  );

  const key = responseAPIKey + observatoryName;
  const apiData = responseAPI.hasOwnProperty(key) ? responseAPI[key] : null;

  if (apiData === null) {
    return null;
  }

  // convert angle_bin_edges from "value1 to value2" -> [value1, value2]
  // const formatBinEdges = (binEdgesString) =>
  //   binEdgesString.split(" to ").map(Number);
  // apiData.forEach((entry) => {
  //   entry.angle_bin_edges = formatBinEdges(entry.angle_bin_edges);
  // });

  return apiData;
}

/**
 * Generate a key to use in `responseAPI` based on the searchParams used
 * @param {string} endpoint
 * @param {Object} searchParams
 * @returns {string}
 */
function makeHistoryResponseAPIKey(endpoint, searchParams) {
  const elements = [endpoint];
  const addIfExists = (key) =>
    Object.hasOwn(searchParams, key) ? elements.push(searchParams[key]) : "";
  addIfExists("model");
  addIfExists("column");

  return elements.join("_") + "History";
}

/**
 * Wrapper to correctly handle arrays passed to URLSearchParams
 * Modified from https://stackoverflow.com/a/69904130
 * @param {Object} searchParams
 * @returns {URLSearchParams}
 */
function makeURLSearchParams(searchParams) {
  let resultParams = new URLSearchParams();

  for (let [key, value] of Object.entries(searchParams)) {
    if (Array.isArray(value)) {
      // handle array parameters
      value.forEach((entry) => resultParams.append(key, entry.toString()));
    } else {
      // handle non-array parameters
      resultParams.append(key, value.toString());
    }
  }
  return resultParams;
}

/**
 * Queries the time domain Ramotswe endpoints for graph data
 * @param {string} observatoryName
 * @param {string} endpoint
 * @param {Object} [apiParams=null]
 * @returns {Object}
 */
export async function getHistoryData(
  observatoryName,
  endpoint,
  startDateTime,
  endDateTime,
  apiParams = null
) {
  // Create the default search params
  const formatDateTime = (dt) => dt.toISO().slice(0, -5);
  const searchParams = {
    start_time: formatDateTime(startDateTime),
    end_time: formatDateTime(endDateTime),
  };
  if (
    ![
      "telemetrytimeframedata",
      "windrose",
      "astronomical/sunlight_hours",
    ].includes(endpoint)
  ) {
    const durationSeconds = endDateTime.diff(startDateTime).as("seconds");
    searchParams.timestep_size = Math.floor(durationSeconds / maxDataPoints);
  }

  // add any requested params
  if (apiParams !== null) {
    Object.assign(searchParams, apiParams);
  }

  const responseAPIKey = makeHistoryResponseAPIKey(endpoint, searchParams);

  await getData(
    responseAPIKey,
    endpoint,
    observatoryName,
    makeURLSearchParams(searchParams)
  );

  return responseAPI[responseAPIKey + observatoryName];
}

/**
 * Function that does an API call to Ramotswe to get a JSON response
 * @param {string} keyName
 * @param {string} endpoint
 * @param {string} observatoryName
 * @param {URLSearchParams} [params=null]
 * @param {path} [path=null]
 * @returns {Object}
 */
export async function getData(
  keyName,
  endpoint,
  observatoryName,
  params = null,
  path = null
) {
  const myHeaders = new Headers([["accept", "application/json"]]);
  let url = `${apiURL}${endpoint}/${observatoryName}`;
  if (path !== null && params !== null) {
    url = url + `/${path}?${params}`;
  } else if (params !== null && path === null) {
    url = url + `?${params}`;
  } else if (params === null && path !== null) {
    url = url + `/${path}`;
  }

  const dataCallback = async (response) => {
    const key = keyName + observatoryName;
    responseAPI[key] = await response.json();
  };

  await makeRequest(url, myHeaders, dataCallback);
}

/**
 * Function that does an API call to Ramotswe to get an image.
 * @param {string} endpoint
 * @param {string} observatoryName
 * @param {string} imageType
 * @returns {Object}
 */
export async function getImage(endpoint, observatoryName, imageType) {
  const myHeaders = new Headers([["accept", "application/json"]]);
  const url = `${apiURL}${endpoint}/${observatoryName}/${imageType}`;

  let tz = "UTC";
  if (imageType === "allsky") {
    tz = observatoryMetadata[observatoryName].TZInfo;
  }

  const imageCallback = async (response) => {
    const ramotsweDatetime = await response.headers.get("image_datetime");
    return new Object({
      blobURL: URL.createObjectURL(await response.blob()),
      exportDatetime: DateTime.fromFormat(
        ramotsweDatetime,
        "yyyy-MM-dd_hh-mm-ss",
        { zone: tz }
      ).toString(),
    });
  };

  return await makeRequest(url, myHeaders, imageCallback);
}

/**
 * Function to do the underlying API call
 * @param {string} url
 * @param {Headers} headers
 * @param {function} responseCallback - function accepting a Response object, will be
 * run and returned assuming a successful request
 * @returns {*}
 */
async function makeRequest(url, headers, responseCallback) {
  try {
    const response = await fetch(url, { headers: headers });
    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }

    return responseCallback(response);
  } catch (error) {
    console.error(error.message);
    return null;
  }
}
